/**
 * templates/categories/cta.js - CTA & Utility Templates
 * Location: /app/frontend/src/admin/page_builder/templates/categories/
 * 
 * Call-to-action, announcements, and utility section templates.
 * Uses Phase 1 primitives: layout presets, backgrounds.
 */

import { LayoutPreset } from '../../layout/presets';
import { BackgroundType, GradientDirection } from '../../layout/backgrounds';

/**
 * CTA Banner - Full-width promotional banner
 * Use cases: Newsletter signup, promotions, announcements
 */
export const cta_banner = {
  id: 'cta_banner',
  name: 'CTA Banner',
  category: 'cta',
  description: 'Full-width call-to-action banner for promotions and signups.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.GRADIENT,
    config: {
      direction: GradientDirection.TO_RIGHT,
      from: '#f97316',
      to: '#ea580c'
    },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.SOLID, BackgroundType.GRADIENT],
  maxBlocks: 3, // Can have multiple CTA banners
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.GRADIENT },
  
  blockType: 'section_cta_banner',
  defaultProps: {
    heading: 'Stay Updated',
    subheading: 'Get the latest comics and updates delivered to your inbox.',
    ctaLabel: 'Subscribe',
    ctaUrl: '#',
    ctaType: 'button', // 'button', 'email-input'
    alignment: 'center', // 'left', 'center', 'right'
    showIcon: true,
    iconType: 'mail', // 'mail', 'bell', 'star', 'arrow'
  },
  
  editableFields: [
    { key: 'heading', label: 'Heading', type: 'text' },
    { key: 'subheading', label: 'Subheading', type: 'text' },
    { key: 'ctaLabel', label: 'CTA Label', type: 'text' },
    { key: 'ctaUrl', label: 'CTA URL', type: 'text' },
    { key: 'ctaType', label: 'CTA Type', type: 'select', options: ['button', 'email-input'] },
    { key: 'alignment', label: 'Alignment', type: 'select', options: ['left', 'center', 'right'] },
    { key: 'showIcon', label: 'Show Icon', type: 'boolean' },
    { key: 'iconType', label: 'Icon Type', type: 'select', options: ['mail', 'bell', 'star', 'arrow'] },
  ],
};

/**
 * Transparency Footer - Platform values/info footer section
 * Use cases: Landing page footer, about section
 */
export const transparency_footer = {
  id: 'transparency_footer',
  name: 'Transparency Footer',
  category: 'cta',
  description: 'Platform values and transparency information section.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.SOLID,
    config: { color: '#000000' },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.SOLID, BackgroundType.NONE],
  maxBlocks: 1, // Only one transparency footer
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.SOLID },
  
  blockType: 'section_transparency_footer',
  defaultProps: {
    heading: 'Built Different',
    showHeading: true,
    items: [
      { icon: 'heart', label: 'Creator First', description: 'Everything we build serves creators.' },
      { icon: 'eye', label: 'Open Development', description: 'Watch us build in public.' },
      { icon: 'shield', label: 'Fair Revenue', description: 'Transparent splits, always.' },
    ],
    footerText: 'Made with love for the culture.',
    showFooterText: true,
    columns: 3,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'footerText', label: 'Footer Text', type: 'text' },
    { key: 'showFooterText', label: 'Show Footer Text', type: 'boolean' },
    { key: 'columns', label: 'Columns', type: 'number', min: 2, max: 4 },
  ],
};

/**
 * Announcement Banner - Admin notice / drop alert / maintenance
 * Use cases: Site-wide announcements, maintenance notices, drop alerts
 */
export const announcement_banner = {
  id: 'announcement_banner',
  name: 'Announcement Banner',
  category: 'cta',
  description: 'Site-wide announcement banner for notices, alerts, and drops.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.SOLID,
    config: { color: 'var(--tc-colors-primary, #f97316)' },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.SOLID],
  maxBlocks: 2, // Can have up to 2 announcement banners
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.SOLID },
  
  blockType: 'section_announcement_banner',
  defaultProps: {
    message: 'New drop coming soon! Stay tuned.',
    linkLabel: 'Learn more',
    linkUrl: '#',
    showLink: true,
    type: 'info', // 'info', 'warning', 'success', 'alert'
    dismissible: true,
    showIcon: true,
    sticky: false, // Stick to top of page
  },
  
  editableFields: [
    { key: 'message', label: 'Message', type: 'text' },
    { key: 'linkLabel', label: 'Link Label', type: 'text' },
    { key: 'linkUrl', label: 'Link URL', type: 'text' },
    { key: 'showLink', label: 'Show Link', type: 'boolean' },
    { key: 'type', label: 'Banner Type', type: 'select', options: ['info', 'warning', 'success', 'alert'] },
    { key: 'dismissible', label: 'Dismissible', type: 'boolean' },
    { key: 'showIcon', label: 'Show Icon', type: 'boolean' },
    { key: 'sticky', label: 'Sticky (Top)', type: 'boolean' },
  ],
  
  // Type-based background colors
  typeBackgrounds: {
    info: { type: BackgroundType.SOLID, config: { color: '#3b82f6' } },
    warning: { type: BackgroundType.SOLID, config: { color: '#eab308' } },
    success: { type: BackgroundType.SOLID, config: { color: '#22c55e' } },
    alert: { type: BackgroundType.SOLID, config: { color: '#ef4444' } },
  },
};

/**
 * Category Spotlight - Media-left/right with optional background
 * Use cases: Genre highlight, category feature, promotional spotlight
 */
export const category_spotlight = {
  id: 'category_spotlight',
  name: 'Category Spotlight',
  category: 'cta',
  description: 'Split layout spotlight with media and content. Great for genre/category highlights.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.MEDIA_RIGHT }, // Can be switched to MEDIA_LEFT
  background: { 
    type: BackgroundType.GRADIENT,
    config: {
      direction: GradientDirection.TO_BOTTOM_RIGHT,
      from: 'rgba(249, 115, 22, 0.1)',
      to: 'transparent'
    },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.MEDIA_LEFT, LayoutPreset.MEDIA_RIGHT, LayoutPreset.SPLIT_50_50],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  maxBlocks: 4, // Can have multiple category spotlights
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.MEDIA_RIGHT },
  defaultBackground: { type: BackgroundType.GRADIENT },
  
  blockType: 'section_category_spotlight',
  defaultProps: {
    heading: 'Action Comics',
    subheading: 'Explore high-octane adventures and thrilling battles.',
    ctaLabel: 'Browse Action',
    ctaUrl: '/browse/action',
    mediaUrl: '',
    mediaAlt: 'Category image',
    mediaPosition: 'right', // 'left', 'right'
    categorySlug: '', // For data binding
    showContentCount: true,
    contentCountLabel: 'comics',
  },
  
  editableFields: [
    { key: 'heading', label: 'Heading', type: 'text' },
    { key: 'subheading', label: 'Subheading', type: 'textarea' },
    { key: 'ctaLabel', label: 'CTA Label', type: 'text' },
    { key: 'ctaUrl', label: 'CTA URL', type: 'text' },
    { key: 'mediaUrl', label: 'Media URL', type: 'image' },
    { key: 'mediaAlt', label: 'Media Alt Text', type: 'text' },
    { key: 'mediaPosition', label: 'Media Position', type: 'select', options: ['left', 'right'] },
    { key: 'categorySlug', label: 'Category Slug', type: 'text' },
    { key: 'showContentCount', label: 'Show Content Count', type: 'boolean' },
    { key: 'contentCountLabel', label: 'Content Label', type: 'text' },
  ],
  
  // Dynamic layout based on mediaPosition
  dynamicLayout: {
    left: { preset: LayoutPreset.MEDIA_LEFT },
    right: { preset: LayoutPreset.MEDIA_RIGHT },
  },
};

export default {
  cta_banner,
  transparency_footer,
  announcement_banner,
  category_spotlight,
};
